// src/features/secret/PkiPanel.tsx
// PKI engine workspace — CA (generate root/intermediate, sign, CRL), roles, certificates
// (issue with SAN fields, revoke). See docs/ux/secret-ia.md section 1b.

import { useEffect, useState, useCallback } from 'react';
import { useI18n, useSecretPki, useSecretCertificate } from '@react/hooks';
import { Button, Input, Textarea, Card, CardHeader, CardTitle, CardContent, ErrorAlert } from '@react/components';
import { Select } from '@/components/Select';
import { StatusBadge } from '@/components/StatusBadge';
import { TypedConfirmDialog } from './TypedConfirmDialog';
import { FormDialog } from '@/components/form';
import { SanListEditor } from './SanListEditor';
import type { PkiRole, Certificate } from '@core/types/secret';
import { certificateStatusVariant, certificateStatusLabelKey, certificateTypeLabelKey, canRevokeCertificate, formatDateTime, certificateConfirmPhrase } from './logic';

// Positive-integer TTL-in-days validation for CA/role forms below (no number steppers — always
// clearable text, validated before submit per docs/ux/14-forms.md).
function parsePositiveIntDays(value: string): number | null {
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const n = parseInt(trimmed, 10);
  return n > 0 ? n : null;
}

function useCopyToClipboard() {
  const [copied, setCopied] = useState(false);
  const copy = useCallback(async (text: string) => {
    try { await navigator.clipboard.writeText(text); } catch { /* clipboard unavailable */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }, []);
  return { copied, copy };
}

// --- CA tab ---

function CaTab({ engineId }: { engineId: string }) {
  const { t } = useI18n();
  const store = useSecretPki();
  const [caPem, setCaPem] = useState<string | null>(null);
  const [crl, setCrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showGenerateRoot, setShowGenerateRoot] = useState(false);
  const [showIntermediate, setShowIntermediate] = useState(false);
  const { copied, copy } = useCopyToClipboard();

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      store.GetCA(engineId).then((r) => setCaPem(r.certificate_pem)).catch(() => setCaPem(null)),
      store.GetCRL(engineId).then((r) => setCrl(r.crl)).catch(() => setCrl(null)),
    ]).finally(() => setLoading(false));
  }, [engineId, store]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader><CardTitle>{t('secret.pki.caTitle')}</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {loading ? (
            <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
          ) : caPem ? (
            <div className="flex items-start gap-2">
              <pre className="flex-1 max-h-40 overflow-auto rounded-md bg-muted p-2 text-xs font-mono">{caPem}</pre>
              <Button variant="secondary" size="sm" onClick={() => copy(caPem)}>{copied ? t('common.copied') : t('common.copy')}</Button>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">{t('secret.pki.noCa')}</p>
          )}
          <div className="flex gap-2 pt-1">
            <Button size="sm" variant="secondary" onClick={() => setShowGenerateRoot((v) => !v)}>{t('secret.pki.generateRoot')}</Button>
            <Button size="sm" variant="secondary" onClick={() => setShowIntermediate((v) => !v)}>{t('secret.pki.generateIntermediate')}</Button>
          </div>
        </CardContent>
      </Card>

      <GenerateRootForm open={showGenerateRoot} engineId={engineId} onDone={() => { setShowGenerateRoot(false); load(); }} onClose={() => setShowGenerateRoot(false)} />
      <IntermediateFlow open={showIntermediate} engineId={engineId} onDone={() => { setShowIntermediate(false); load(); }} onClose={() => setShowIntermediate(false)} />

      <Card>
        <CardHeader><CardTitle>{t('secret.pki.crlTitle')}</CardTitle></CardHeader>
        <CardContent>
          {crl ? (
            <pre className="max-h-32 overflow-auto rounded-md bg-muted p-2 text-xs font-mono">{crl}</pre>
          ) : (
            <p className="text-sm text-muted-foreground">{t('secret.pki.noCrl')}</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function GenerateRootForm({ open, engineId, onDone, onClose }: { open: boolean; engineId: string; onDone: () => void; onClose: () => void }) {
  const { t } = useI18n();
  const store = useSecretPki();
  const [commonName, setCommonName] = useState('');
  const [ttlDays, setTtlDays] = useState('3650');
  const [ttlError, setTtlError] = useState<string | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<unknown>(null);

  const dirty = !!(commonName.trim() || ttlDays.trim() !== '3650');

  const reset = () => {
    setCommonName('');
    setTtlDays('3650');
    setTtlError(undefined);
    setSubmitError(null);
  };

  const close = () => { reset(); onClose(); };

  const submit = async () => {
    const days = parsePositiveIntDays(ttlDays);
    if (!commonName.trim()) return;
    if (days == null) {
      setTtlError(t('secret.validation.positiveIntegerRequired'));
      return;
    }
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await store.GenerateRoot(engineId, {
        engine_id: engineId,
        common_name: commonName.trim(),
        ttl_seconds: days * 86400,
        organization: [],
        ou: [],
        country: [],
      });
      reset();
      onDone();
    } catch (err) {
      setSubmitError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={close}
      onSubmit={submit}
      submitLabel={t('secret.pki.generateRoot')}
      canSubmit={!!commonName.trim()}
      isSubmitting={isSubmitting}
      dirty={dirty}
      aria-label={t('secret.pki.generateRoot')}
    >
      <div className="space-y-3 px-3.5 pb-4 pt-2">
        {submitError != null && <ErrorAlert error={submitError} dismissible onDismiss={() => setSubmitError(null)} />}
        <p className="rounded-md border border-warning/50 bg-warning/10 p-2 text-xs text-muted-foreground">{t('secret.pki.generateRootWarning')}</p>
        <Input label={t('secret.pki.field.commonName')} value={commonName} onChange={(e) => setCommonName(e.target.value)} placeholder="Example Root CA" data-autofocus />
        <Input
          label={t('secret.pki.field.ttlDays')}
          value={ttlDays}
          onChange={(e) => { setTtlDays(e.target.value); setTtlError(undefined); }}
          inputMode="numeric"
          error={ttlError}
        />
      </div>
    </FormDialog>
  );
}

function IntermediateFlow({ open, engineId, onDone, onClose }: { open: boolean; engineId: string; onDone: () => void; onClose: () => void }) {
  const { t } = useI18n();
  const store = useSecretPki();
  const [commonName, setCommonName] = useState('');
  const [csr, setCsr] = useState<string | null>(null);
  const [signTtlDays, setSignTtlDays] = useState('1825');
  const [ttlError, setTtlError] = useState<string | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { copied, copy } = useCopyToClipboard();

  const dirty = !!(commonName.trim() || csr);

  const reset = () => {
    setCommonName('');
    setCsr(null);
    setSignTtlDays('1825');
    setTtlError(undefined);
  };

  const close = () => { reset(); onClose(); };

  const handleGenerateCsr = async () => {
    if (!commonName.trim()) return;
    setIsSubmitting(true);
    try {
      const resp = await store.GenerateIntermediate(engineId, {
        engine_id: engineId, common_name: commonName.trim(), organization: [], ou: [], country: [],
      });
      setCsr(resp.csr_pem);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSign = async () => {
    if (!csr) return;
    const days = parsePositiveIntDays(signTtlDays);
    if (days == null) {
      setTtlError(t('secret.validation.positiveIntegerRequired'));
      return;
    }
    setIsSubmitting(true);
    try {
      await store.SignIntermediate(engineId, {
        engine_id: engineId, csr_pem: csr, common_name: commonName.trim(), ttl_seconds: days * 86400,
      });
      reset();
      onDone();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={close}
      onSubmit={csr ? handleSign : handleGenerateCsr}
      submitLabel={csr ? t('secret.pki.signIntermediate') : t('secret.pki.generateCsr')}
      canSubmit={csr ? true : !!commonName.trim()}
      isSubmitting={isSubmitting}
      dirty={dirty}
      aria-label={t('secret.pki.generateIntermediate')}
    >
      <div className="space-y-3 px-3.5 pb-4 pt-2">
        <p className="text-xs text-muted-foreground">{t('secret.pki.intermediateHelp')}</p>
        <Input
          label={t('secret.pki.field.commonName')}
          value={commonName}
          onChange={(e) => setCommonName(e.target.value)}
          placeholder="Example Intermediate CA"
          disabled={!!csr}
          data-autofocus
        />
        {csr && (
          <>
            <div className="flex items-start gap-2">
              <pre className="flex-1 max-h-32 overflow-auto rounded-md bg-muted p-2 text-xs font-mono">{csr}</pre>
              <Button type="button" variant="secondary" size="sm" onClick={() => copy(csr)}>{copied ? t('common.copied') : t('common.copy')}</Button>
            </div>
            <p className="text-xs text-muted-foreground">{t('secret.pki.signHelp')}</p>
            <Input
              label={t('secret.pki.field.ttlDays')}
              value={signTtlDays}
              onChange={(e) => { setSignTtlDays(e.target.value); setTtlError(undefined); }}
              inputMode="numeric"
              error={ttlError}
            />
          </>
        )}
      </div>
    </FormDialog>
  );
}

// --- Roles tab ---

function RolesTab({ engineId }: { engineId: string }) {
  const { t } = useI18n();
  const store = useSecretPki();
  const [roles, setRoles] = useState<PkiRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<PkiRole | null>(null);
  const [isActing, setIsActing] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [detail, setDetail] = useState<PkiRole | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    store.ListPkiRoles(engineId).then((r) => setRoles(r.roles ?? [])).catch(() => setRoles([])).finally(() => setLoading(false));
  }, [engineId, store]);

  useEffect(() => { load(); }, [load]);

  const toggleExpand = (role: PkiRole) => {
    if (expanded === role.name) { setExpanded(null); setDetail(null); return; }
    setExpanded(role.name);
    setDetail(null);
    store.GetPkiRole(engineId, role.name).then((r) => setDetail(r.role ?? null)).catch(() => {});
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    setIsActing(true);
    try { await store.DeletePkiRole(engineId, deleteTarget.name); setDeleteTarget(null); load(); } finally { setIsActing(false); }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button size="sm" onClick={() => setShowCreate(true)}>{t('secret.pki.createRole')}</Button>
      </div>
      <CreateRoleForm open={showCreate} engineId={engineId} onCreated={() => { setShowCreate(false); load(); }} onClose={() => setShowCreate(false)} />
      <TypedConfirmDialog
        open={!!deleteTarget}
        title={t('secret.pki.deleteRoleTitle')}
        description={t('secret.pki.deleteRoleDescription')}
        phrase={deleteTarget?.name ?? ''}
        confirmLabel={t('common.delete')}
        isConfirming={isActing}
        onConfirm={doDelete}
        onCancel={() => setDeleteTarget(null)}
      />
      {loading ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : roles.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('secret.pki.noRoles')}</p>
      ) : (
        <ul className="divide-y divide-border rounded-lg border border-border">
          {roles.map((role) => (
            <li key={role.name} className="p-3">
              <div className="flex items-center justify-between">
                <button onClick={() => toggleExpand(role)} className="text-left">
                  <p className="text-sm font-medium text-foreground">{role.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {role.allowed_domains.length > 0 ? role.allowed_domains.join(', ') : t('secret.pki.anyDomain')}
                  </p>
                </button>
                <Button variant="ghost" size="sm" className="text-destructive" onClick={() => setDeleteTarget(role)}>{t('common.delete')}</Button>
              </div>
              {expanded === role.name && (
                <div className="mt-2 rounded-md bg-muted/40 p-2 text-xs text-muted-foreground">
                  {detail ? (
                    <>
                      <div>{t('secret.pki.field.maxTtlDays')}: {Math.round((detail.max_ttl_seconds ?? 0) / 86400)}</div>
                      <div>{t('secret.pki.field.allowSubdomains')}: {detail.allow_subdomains ? t('common.yes') : t('common.no')}</div>
                      <div>key_type: {detail.key_type || '—'}</div>
                    </>
                  ) : t('common.loading')}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CreateRoleForm({ open, engineId, onCreated, onClose }: { open: boolean; engineId: string; onCreated: () => void; onClose: () => void }) {
  const { t } = useI18n();
  const store = useSecretPki();
  const [name, setName] = useState('');
  const [allowedDomains, setAllowedDomains] = useState<string[]>([]);
  const [allowSubdomains, setAllowSubdomains] = useState(true);
  const [maxTtlDays, setMaxTtlDays] = useState('365');
  const [ttlError, setTtlError] = useState<string | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<unknown>(null);

  const dirty = !!(name.trim() || allowedDomains.length > 0 || maxTtlDays.trim() !== '365' || !allowSubdomains);

  const reset = () => {
    setName('');
    setAllowedDomains([]);
    setAllowSubdomains(true);
    setMaxTtlDays('365');
    setTtlError(undefined);
    setSubmitError(null);
  };

  const close = () => { reset(); onClose(); };

  const submit = async () => {
    if (!name.trim()) return;
    const days = parsePositiveIntDays(maxTtlDays);
    if (days == null) {
      setTtlError(t('secret.validation.positiveIntegerRequired'));
      return;
    }
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await store.CreatePkiRole(engineId, {
        engine_id: engineId,
        role: {
          name: name.trim(),
          allowed_domains: allowedDomains,
          allow_subdomains: allowSubdomains,
          max_ttl_seconds: days * 86400,
        } as PkiRole,
      });
      reset();
      onCreated();
    } catch (err) {
      setSubmitError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={close}
      onSubmit={submit}
      submitLabel={t('common.create')}
      canSubmit={!!name.trim()}
      isSubmitting={isSubmitting}
      dirty={dirty}
      aria-label={t('secret.pki.createRole')}
    >
      <div className="space-y-3 px-3.5 pb-4 pt-2">
        {submitError != null && <ErrorAlert error={submitError} dismissible onDismiss={() => setSubmitError(null)} />}
        <Input label={t('secret.pki.field.roleName')} value={name} onChange={(e) => setName(e.target.value)} data-autofocus />
        <div>
          <label className="mb-1 block text-sm text-foreground">{t('secret.pki.field.allowedDomains')}</label>
          <SanListEditor value={allowedDomains} onChange={setAllowedDomains} placeholder="example.com" aria-label={t('secret.pki.field.allowedDomains')} />
          <p className="mt-1 text-xs text-muted-foreground">{t('secret.pki.field.allowedDomainsHelp')}</p>
        </div>
        <label className="flex items-center gap-2 cursor-pointer text-sm text-foreground">
          <input type="checkbox" checked={allowSubdomains} onChange={(e) => setAllowSubdomains(e.target.checked)} className="h-4 w-4 rounded border-input text-primary focus:ring-ring" />
          {t('secret.pki.field.allowSubdomains')}
        </label>
        <Input
          label={t('secret.pki.field.maxTtlDays')}
          value={maxTtlDays}
          onChange={(e) => { setMaxTtlDays(e.target.value); setTtlError(undefined); }}
          inputMode="numeric"
          error={ttlError}
        />
      </div>
    </FormDialog>
  );
}

// --- Certificates tab ---

function CertificatesTab({ engineId }: { engineId: string }) {
  const { t } = useI18n();
  const store = useSecretCertificate();
  const pkiStore = useSecretPki();
  const [certs, setCerts] = useState<Certificate[]>([]);
  const [roles, setRoles] = useState<PkiRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [showIssue, setShowIssue] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<Certificate | null>(null);
  const [isActing, setIsActing] = useState(false);
  const [expandedSerial, setExpandedSerial] = useState<string | null>(null);
  const [pem, setPem] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      store.ListCertificates(engineId).then((r) => setCerts(r.certificates ?? [])).catch(() => setCerts([])),
      pkiStore.ListPkiRoles(engineId).then((r) => setRoles(r.roles ?? [])).catch(() => setRoles([])),
    ]).finally(() => setLoading(false));
  }, [engineId, store, pkiStore]);

  useEffect(() => { load(); }, [load]);

  const toggleExpand = (cert: Certificate) => {
    if (expandedSerial === cert.serial_number) { setExpandedSerial(null); setPem(null); return; }
    setExpandedSerial(cert.serial_number);
    setPem(null);
    store.GetCertificate(engineId, cert.serial_number).then((c) => setPem(c.certificate_pem)).catch(() => {});
  };

  const doRevoke = async (reason: string) => {
    if (!revokeTarget) return;
    setIsActing(true);
    try {
      await store.RevokeCertificate(engineId, { engine_id: engineId, serial_number: revokeTarget.serial_number, reason });
      setRevokeTarget(null);
      load();
    } finally {
      setIsActing(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button size="sm" onClick={() => setShowIssue(true)} disabled={roles.length === 0}>{t('secret.pki.issueCertificate')}</Button>
      </div>
      {roles.length === 0 && !loading && <p className="text-xs text-muted-foreground">{t('secret.pki.needRoleFirst')}</p>}
      <IssueCertificateForm
        open={showIssue}
        engineId={engineId}
        roles={roles}
        onIssued={() => { setShowIssue(false); load(); }}
        onClose={() => setShowIssue(false)}
      />

      <RevokeForm cert={revokeTarget} onConfirm={doRevoke} onCancel={() => setRevokeTarget(null)} isLoading={isActing} />

      {loading ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : certs.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('secret.pki.noCertificates')}</p>
      ) : (
        <ul className="divide-y divide-border rounded-lg border border-border">
          {certs.map((cert) => (
            <li key={cert.serial_number} className="p-3">
              <div className="flex items-center justify-between">
                <button onClick={() => toggleExpand(cert)} className="text-left">
                  <p className="text-sm font-medium text-foreground">{cert.common_name}</p>
                  <p className="text-xs text-muted-foreground font-mono">{cert.serial_number}</p>
                  <p className="text-xs text-muted-foreground">
                    {t(certificateTypeLabelKey(cert.certificate_type))} · {t('secret.pki.expiresOn', { date: formatDateTime(cert.not_after) })}
                  </p>
                </button>
                <div className="flex items-center gap-2">
                  <StatusBadge variant={certificateStatusVariant(cert.status)} label={t(certificateStatusLabelKey(cert.status))} size="sm" />
                  {canRevokeCertificate(cert) && (
                    <Button variant="ghost" size="sm" className="text-destructive" onClick={() => setRevokeTarget(cert)}>{t('secret.pki.revoke')}</Button>
                  )}
                </div>
              </div>
              {expandedSerial === cert.serial_number && (
                <pre className="mt-2 max-h-32 overflow-auto rounded-md bg-muted p-2 text-xs font-mono">
                  {pem ?? t('common.loading')}
                </pre>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function IssueCertificateForm({ open, engineId, roles, onIssued, onClose }: {
  open: boolean; engineId: string; roles: PkiRole[]; onIssued: () => void; onClose: () => void;
}) {
  const { t } = useI18n();
  const store = useSecretCertificate();
  const [roleName, setRoleName] = useState(roles[0]?.name ?? '');
  const [commonName, setCommonName] = useState('');
  const [dnsSans, setDnsSans] = useState<string[]>([]);
  const [ipSans, setIpSans] = useState<string[]>([]);
  const [uriSans, setUriSans] = useState<string[]>([]);
  const [emailSans, setEmailSans] = useState<string[]>([]);
  const [issued, setIssued] = useState<{ certificate_pem: string; private_key_pem?: string } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<unknown>(null);
  const { copied, copy } = useCopyToClipboard();

  // Keep the role select in sync once roles load in (dialog can open before ListPkiRoles resolves).
  useEffect(() => {
    if (!roleName && roles[0]) setRoleName(roles[0].name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roles]);

  const dirty = !!(commonName.trim() || dnsSans.length || ipSans.length || uriSans.length || emailSans.length);

  const reset = () => {
    setCommonName('');
    setDnsSans([]);
    setIpSans([]);
    setUriSans([]);
    setEmailSans([]);
    setIssued(null);
    setSubmitError(null);
  };

  const close = () => { reset(); onClose(); };

  const submit = async () => {
    if (issued) {
      reset();
      onIssued();
      return;
    }
    if (!roleName || !commonName.trim()) return;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const resp = await store.IssueCertificate(engineId, roleName, {
        engine_id: engineId,
        role_name: roleName,
        common_name: commonName.trim(),
        dns_sans: dnsSans,
        ip_sans: ipSans,
        uri_sans: uriSans,
        email_sans: emailSans,
      });
      if (resp.certificate) {
        setIssued({ certificate_pem: resp.certificate.certificate_pem, private_key_pem: resp.private_key_pem });
      } else {
        reset();
        onIssued();
      }
    } catch (err) {
      setSubmitError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={close}
      onSubmit={submit}
      submitLabel={issued ? t('common.done') : t('secret.pki.issue')}
      canSubmit={issued ? true : !!roleName && !!commonName.trim()}
      isSubmitting={isSubmitting}
      dirty={!issued && dirty}
      width="lg"
      aria-label={t('secret.pki.issueCertificate')}
    >
      <div className="space-y-3 px-3.5 pb-4 pt-2">
        {issued ? (
          <>
            <p className="text-xs text-muted-foreground">{t('secret.pki.issuedWarning')}</p>
            {issued.private_key_pem && (
              <div className="flex items-start gap-2">
                <pre className="flex-1 max-h-32 overflow-auto rounded-md bg-muted p-2 text-xs font-mono">{issued.private_key_pem}</pre>
                <Button type="button" variant="secondary" size="sm" onClick={() => copy(issued.private_key_pem!)}>{copied ? t('common.copied') : t('common.copy')}</Button>
              </div>
            )}
            <pre className="max-h-32 overflow-auto rounded-md bg-muted p-2 text-xs font-mono">{issued.certificate_pem}</pre>
          </>
        ) : (
          <>
            {submitError != null && <ErrorAlert error={submitError} dismissible onDismiss={() => setSubmitError(null)} />}
            <Select
              label={t('secret.pki.field.role')}
              value={roleName}
              onChange={(e) => setRoleName(e.target.value)}
              options={roles.map((r) => ({ value: r.name, label: r.name }))}
            />
            <Input label={t('secret.pki.field.commonName')} value={commonName} onChange={(e) => setCommonName(e.target.value)} placeholder="app.example.com" data-autofocus />
            <div>
              <label className="mb-1 block text-sm text-foreground">{t('secret.pki.field.dnsSans')}</label>
              <SanListEditor value={dnsSans} onChange={setDnsSans} placeholder="www.example.com" aria-label={t('secret.pki.field.dnsSans')} />
            </div>
            <div>
              <label className="mb-1 block text-sm text-foreground">{t('secret.pki.field.ipSans')}</label>
              <SanListEditor value={ipSans} onChange={setIpSans} placeholder="10.0.0.1" aria-label={t('secret.pki.field.ipSans')} />
            </div>
            <div>
              <label className="mb-1 block text-sm text-foreground">{t('secret.pki.field.uriSans')}</label>
              <SanListEditor value={uriSans} onChange={setUriSans} placeholder="spiffe://example.com/app" aria-label={t('secret.pki.field.uriSans')} />
            </div>
            <div>
              <label className="mb-1 block text-sm text-foreground">{t('secret.pki.field.emailSans')}</label>
              <SanListEditor value={emailSans} onChange={setEmailSans} placeholder="admin@example.com" aria-label={t('secret.pki.field.emailSans')} />
            </div>
          </>
        )}
      </div>
    </FormDialog>
  );
}

function RevokeForm({ cert, onConfirm, onCancel, isLoading }: {
  cert: Certificate | null; onConfirm: (reason: string) => void; onCancel: () => void; isLoading: boolean;
}) {
  const { t } = useI18n();
  const [reason, setReason] = useState('');

  useEffect(() => { if (cert) setReason(''); }, [cert]);

  if (!cert) return null;

  return (
    <TypedConfirmDialog
      open={!!cert}
      title={t('secret.pki.revokeTitle', { cn: cert.common_name })}
      phrase={certificateConfirmPhrase(cert)}
      confirmLabel={t('secret.pki.revoke')}
      isConfirming={isLoading}
      onConfirm={() => onConfirm(reason)}
      onCancel={onCancel}
    >
      <Textarea label={t('secret.pki.revokeReason')} value={reason} onChange={(e) => setReason(e.target.value)} />
    </TypedConfirmDialog>
  );
}

// --- Top-level ---

const TABS = ['ca', 'roles', 'certificates'] as const;
type PkiTab = typeof TABS[number];

export function PkiPanel({ engineId }: { engineId: string }) {
  const { t } = useI18n();
  const [tab, setTab] = useState<PkiTab>('ca');

  return (
    <div className="flex-1 overflow-y-auto p-6 space-y-4">
      <div role="tablist" className="flex gap-1 border-b border-border">
        {TABS.map((v) => (
          <button
            key={v}
            role="tab"
            aria-selected={tab === v}
            onClick={() => setTab(v)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm ${tab === v ? 'border-primary font-medium text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          >
            {t(`secret.pki.tab.${v}`)}
          </button>
        ))}
      </div>
      {tab === 'ca' && <CaTab engineId={engineId} />}
      {tab === 'roles' && <RolesTab engineId={engineId} />}
      {tab === 'certificates' && <CertificatesTab engineId={engineId} />}
    </div>
  );
}
